/**
 * Report-type chooser — large card grid for picking a report to file.
 * Mobile-first; restricted types (weapons discharge) are hidden for users
 * without the required role.
 */
import { createFileRoute, Link } from "@tanstack/react-router";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ChevronRight, ShieldAlert } from "lucide-react";
import { REPORT_TYPES, canCreateReport } from "@/lib/report-types";

export const Route = createFileRoute("/_authenticated/reports/new/")({
  component: NewReportChooser,
  head: () => ({
    meta: [{ title: "New report — MissionReady" }],
  }),
});

function NewReportChooser() {
  const { currentRole } = useAuth();
  return (
    <div className="mx-auto max-w-3xl px-4 pb-24 pt-6 md:px-8 md:py-8">
      <Button asChild variant="ghost" size="sm" className="mb-4 -ml-2">
        <Link to="/reports">
          <ArrowLeft className="mr-1 h-4 w-4" /> All reports
        </Link>
      </Button>

      <h1 className="font-display text-2xl font-bold md:text-3xl">New report</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Choose the type of report you need to file.
      </p>

      <ul className="mt-6 grid gap-3">
        {REPORT_TYPES.map((meta) => {
          const allowed = canCreateReport(meta, currentRole);
          const Icon = meta.icon;
          const Inner = (
            <div
              className={`flex items-start gap-4 rounded-xl border bg-card p-4 transition-colors ${
                allowed
                  ? "border-border hover:border-primary/40 hover:bg-muted/30"
                  : "border-dashed border-border/60 opacity-60"
              }`}
            >
              <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-lg bg-primary/15 text-primary">
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium">{meta.label}</span>
                  {meta.restrictedRecord && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-destructive/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-destructive">
                      <ShieldAlert className="h-3 w-3" /> Restricted
                    </span>
                  )}
                </div>
                <p className="mt-1 text-xs text-muted-foreground">{meta.description}</p>
                {!allowed && (
                  <p className="mt-2 text-[11px] text-destructive">
                    Requires supervisor or admin role.
                  </p>
                )}
              </div>
              <ChevronRight className="mt-3 h-4 w-4 text-muted-foreground" />
            </div>
          );

          return (
            <li key={meta.type}>
              {allowed ? (
                <Link
                  to="/reports/new/$type"
                  params={{ type: meta.type }}
                  className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
                >
                  {Inner}
                </Link>
              ) : (
                <div aria-disabled="true">{Inner}</div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
